// 단일/일괄 등록 폼 컴포넌트
// src/pages/admin/components/draw/DrawForm.tsx

import type { DrawMode } from '@/pages/admin/components/draw/types';

interface DrawFormProps {
  mode: DrawMode;
  submitting: boolean;

  // 단일
  drawId: string;
  drawDate: string;
  drawNumbers: string;
  drawBonus: string;
  drawPrize: string;
  drawWinners: string;
  onDrawIdChange: (value: string) => void;
  onDrawDateChange: (value: string) => void;
  onDrawNumbersChange: (value: string) => void;
  onDrawBonusChange: (value: string) => void;
  onDrawPrizeChange: (value: string) => void;
  onDrawWinnersChange: (value: string) => void;
  onSingleSubmit: () => void;

  // 일괄
  bulkText: string;
  onBulkTextChange: (value: string) => void;
  onBulkSubmit: () => void;
}

export default function DrawForm({
  mode,
  submitting,
  drawId, drawDate, drawNumbers, drawBonus, drawPrize, drawWinners,
  onDrawIdChange, onDrawDateChange, onDrawNumbersChange, onDrawBonusChange,
  onDrawPrizeChange, onDrawWinnersChange,
  onSingleSubmit,
  bulkText, onBulkTextChange, onBulkSubmit,
}: DrawFormProps) {
  if (mode === 'single') {
    return (
      <div className="bg-background-50 rounded-2xl border border-background-200 p-6">
        <h4 className="font-heading text-sm text-foreground-700 mb-4">단일 회차 등록</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">회차</label>
            <input
              type="number"
              value={drawId}
              onChange={(e) => onDrawIdChange(e.target.value)}
              placeholder="예: 1083"
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">추첨일</label>
            <input
              type="date"
              value={drawDate}
              onChange={(e) => onDrawDateChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">당첨번호 (6개)</label>
            <input
              type="text"
              value={drawNumbers}
              onChange={(e) => onDrawNumbersChange(e.target.value)}
              placeholder="1, 5, 12, 23, 34, 45"
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">보너스 번호</label>
            <input
              type="number"
              value={drawBonus}
              onChange={(e) => onDrawBonusChange(e.target.value)}
              placeholder="7"
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">1등 당첨금 (원)</label>
            <input
              type="text"
              value={drawPrize}
              onChange={(e) => onDrawPrizeChange(e.target.value)}
              placeholder="3000000000"
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">1등 당첨자 수</label>
            <input
              type="number"
              value={drawWinners}
              onChange={(e) => onDrawWinnersChange(e.target.value)}
              placeholder="10"
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>
        <button
          onClick={onSingleSubmit}
          disabled={submitting}
          className="mt-4 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
        >
          {submitting ? '등록 중...' : '등록하기'}
        </button>
      </div>
    );
  }

  // bulk mode
  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 p-6">
      <h4 className="font-heading text-sm text-foreground-700 mb-2">일괄 등록</h4>
      <p className="text-xs text-foreground-400 mb-4">
        한 줄에 하나씩: 회차, 추첨일(YYYY-MM-DD), 번호6개(쉼표구분), 보너스<br />
        예: 1083, 2025-08-09, 1, 5, 12, 23, 34, 45, 7
      </p>
      <textarea
        value={bulkText}
        onChange={(e) => onBulkTextChange(e.target.value)}
        placeholder={`1083, 2025-08-09, 1, 5, 12, 23, 34, 45, 7\n1084, 2025-08-16, 3, 8, 15, 27, 38, 44, 9`}
        rows={8}
        className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 font-mono"
      />
      <button
        onClick={onBulkSubmit}
        disabled={submitting}
        className="mt-4 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
      >
        {submitting ? '등록 중...' : '일괄 등록하기'}
      </button>
    </div>
  );
}